/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { useForm } from "react-hook-form";
import { Box, Button, Text } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { DetailsTitle } from "~/components/Styled/Common";
import { showSuccess } from "~/components/Toast";
import { Environment } from "~/models/environment";
import { useAxios, generateActionSuccessText } from "~/tools";
import { SwitchToDefaultValue } from "./SwitchToDefaultValue";

type BillingRulesFormEnvironmentProps = {
  id: string;
  environment: Environment;
  name?: string;
  refetch: () => void;
  dateError: boolean;
  canEdit?: boolean;
};

const checkValue = (switchValue: boolean, formValue: string) =>
  switchValue ? null : formValue;

type Values = {
  serviceRequestsOfficeDefault: boolean;
  serviceRequestsOffice: string;
  serviceRequestsOutsideOfficeAfterDefault: boolean;
  serviceRequestsOutsideOfficeAfter: string;
  serviceRequestsOutsideOfficeBeforeDefault: boolean;
  serviceRequestsOutsideOfficeBefore: string;
  incidentsOfficeDefault: boolean;
  incidentsOffice: string;
  incidentOutsideOfficeHoursAfterDefault: boolean;
  incidentOutsideOfficeHoursAfter: string;
  incidentOutsideOfficeHoursBeforeDefault: boolean;
  incidentOutsideOfficeHoursBefore: string;
};

function BillingRulesFormEnvironment({
  id,
  name,
  environment,
  refetch,
  dateError,
  canEdit = true,
}: BillingRulesFormEnvironmentProps) {
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async (values: Values) => {
    const organizeValues = {
      timeline: {
        softLaunch: environment.timeline?.softLaunch,
        goLive: environment.timeline?.goLive,
        offboarding: environment.timeline?.offboarding,
      },
      attachedServiceLevel: environment.attachedServiceLevel?.serviceLevel?.id
        ? {
            capacityBasedFeeType:
              environment.attachedServiceLevel?.capacityBasedFeeType,
            serviceLevelId: environment.attachedServiceLevel?.serviceLevel?.id,
            capacityBasedPercentageFee:
              environment.attachedServiceLevel?.capacityBasedPercentageFee,
          }
        : null,
      billingRules: {
        serviceRequests: {
          officeHours: {
            pricePerHour: checkValue(
              values.serviceRequestsOfficeDefault,
              values.serviceRequestsOffice
            ),
          },
          outsideOfficeHoursAfterMidnight: {
            pricePerHour: checkValue(
              values.serviceRequestsOutsideOfficeAfterDefault,
              values.serviceRequestsOutsideOfficeAfter
            ),
          },
          outsideOfficeHoursBeforeMidnight: {
            pricePerHour: checkValue(
              values.serviceRequestsOutsideOfficeBeforeDefault,
              values.serviceRequestsOutsideOfficeBefore
            ),
          },
        },
        incidents: {
          officeHours: {
            pricePerHour: checkValue(
              values.incidentsOfficeDefault,
              values.incidentsOffice
            ),
          },
          outsideOfficeHoursAfterMidnight: {
            pricePerHour: checkValue(
              values.incidentOutsideOfficeHoursAfterDefault,
              values.incidentOutsideOfficeHoursAfter
            ),
          },
          outsideOfficeHoursBeforeMidnight: {
            pricePerHour: checkValue(
              values.incidentOutsideOfficeHoursBeforeDefault,
              values.incidentOutsideOfficeHoursBefore
            ),
          },
        },
      },
      status: environment.status,
      cfRepoPath: environment.cfRepoPath,
      name: environment.name,
    };
    try {
      await Axios().put(`/environments/${id}`, organizeValues);
      showSuccess(
        generateActionSuccessText(`Environment ${name}`)()("updated")()
      );
    } catch (e) {
      showError(e.response);
    } finally {
      refetch();
    }
  };
  const { register, handleSubmit, errors, watch, setValue } = useForm();

  const watcher = watch();

  const serviceRequestsOfficeDefault =
    environment?.billingRules?.serviceRequests?.officeHours
      ?.defaultPricePerHour;
  const serviceRequestsOfficePrice =
    environment?.billingRules?.serviceRequests?.officeHours?.pricePerHour;

  const serviceRequestsOutsideOfficeHoursAfterDefault =
    environment?.billingRules?.serviceRequests?.outsideOfficeHoursAfterMidnight
      ?.defaultPricePerHour;
  const serviceRequestsOutsideOfficeHoursAfterPrice =
    environment?.billingRules?.serviceRequests?.outsideOfficeHoursAfterMidnight
      ?.pricePerHour;
  const serviceRequestsOutsideOfficeHoursBeforeDefault =
    environment?.billingRules?.serviceRequests?.outsideOfficeHoursBeforeMidnight
      ?.defaultPricePerHour;
  const serviceRequestsOutsideOfficeHoursBeforePrice =
    environment?.billingRules?.serviceRequests?.outsideOfficeHoursBeforeMidnight
      ?.pricePerHour;

  const incidentsOfficeDefault =
    environment?.billingRules?.incidents?.officeHours?.defaultPricePerHour;
  const incidentsOfficePrice =
    environment?.billingRules?.incidents?.officeHours?.pricePerHour;

  const incidentsOutsideOfficeHoursAfterDefault =
    environment?.billingRules?.incidents?.outsideOfficeHoursAfterMidnight
      ?.defaultPricePerHour;
  const incidentsOutsideOfficeHoursAfterPrice =
    environment?.billingRules?.incidents?.outsideOfficeHoursAfterMidnight
      ?.pricePerHour;

  const incidentsOutsideOfficeHoursBeforeDefault =
    environment?.billingRules?.incidents?.outsideOfficeHoursBeforeMidnight
      ?.defaultPricePerHour;
  const incidentsOutsideOfficeHoursBeforePrice =
    environment?.billingRules?.incidents?.outsideOfficeHoursBeforeMidnight
      ?.pricePerHour;

  React.useEffect(() => {
    register({
      name: "isCapacityBasedFeeDefault",
      value: false,
    });
    register({
      name: "serviceRequestsOfficeDefault",
      value: false,
    });

    register({
      name: "serviceRequestsOutsideOfficeAfterDefault",
      value: false,
    });
    register({
      name: "serviceRequestsOutsideOfficeBeforeDefault",
      value: false,
    });
    register({ name: "incidentsOfficeDefault", value: false });
    register({ name: "incidentOutsideOfficeHoursAfterDefault", value: false });
    register({ name: "incidentOutsideOfficeHoursBeforeDefault", value: false });
  });

  React.useEffect(() => {
    serviceRequestsOfficePrice
      ? setValue("serviceRequestsOfficeDefault", false)
      : setValue("serviceRequestsOfficeDefault", true);
  }, [serviceRequestsOfficePrice, setValue]);

  React.useEffect(() => {
    serviceRequestsOutsideOfficeHoursAfterPrice
      ? setValue("serviceRequestsOutsideOfficeAfterDefault", false)
      : setValue("serviceRequestsOutsideOfficeAfterDefault", true);
  }, [serviceRequestsOutsideOfficeHoursAfterPrice, setValue]);

  React.useEffect(() => {
    serviceRequestsOutsideOfficeHoursBeforePrice
      ? setValue("serviceRequestsOutsideOfficeBeforeDefault", false)
      : setValue("serviceRequestsOutsideOfficeBeforeDefault", true);
  }, [serviceRequestsOutsideOfficeHoursBeforePrice, setValue]);

  React.useEffect(() => {
    incidentsOfficePrice
      ? setValue("incidentsOfficeDefault", false)
      : setValue("incidentsOfficeDefault", true);
  }, [incidentsOfficePrice, setValue]);

  React.useEffect(() => {
    incidentsOutsideOfficeHoursAfterPrice
      ? setValue("incidentOutsideOfficeHoursAfterDefault", false)
      : setValue("incidentOutsideOfficeHoursAfterDefault", true);
  }, [incidentsOutsideOfficeHoursAfterPrice, setValue]);

  React.useEffect(() => {
    incidentsOutsideOfficeHoursBeforePrice
      ? setValue("incidentOutsideOfficeHoursBeforeDefault", false)
      : setValue("incidentOutsideOfficeHoursBeforeDefault", true);
  }, [incidentsOutsideOfficeHoursBeforePrice, setValue]);

  React.useEffect(() => {
    register({
      name: "isDisableServiceLevel",
    });
  }, [register]);

  return (
    <Box spacing="spacing04">
      <Text weight="medium">Billing Rules</Text>
      <Row>
        <Col
          xs={4}
          sm={4}
          md={4}
          lg={6}
          data-cy="billing-rules-service-request"
        >
          <DetailsTitle css={{ margin: "2rem 0 1rem 0rem" }}>
            Service Request
          </DetailsTitle>

          <SwitchToDefaultValue
            label="Office Hours"
            name="serviceRequestsOffice"
            nameDefault="serviceRequestsOfficeDefault"
            defaultPrice={serviceRequestsOfficeDefault}
            price={serviceRequestsOfficePrice}
            watcher={watcher}
            setValue={setValue}
            register={register}
            errors={errors}
            canEdit={canEdit}
          />
          <SwitchToDefaultValue
            label="Outside Office Hours - Till 00:00"
            name="serviceRequestsOutsideOfficeBefore"
            nameDefault="serviceRequestsOutsideOfficeBeforeDefault"
            defaultPrice={serviceRequestsOutsideOfficeHoursBeforeDefault}
            price={serviceRequestsOutsideOfficeHoursBeforePrice}
            watcher={watcher}
            setValue={setValue}
            register={register}
            errors={errors}
            canEdit={canEdit}
          />

          <SwitchToDefaultValue
            label="Outside Office Hours - After 00:00"
            name="serviceRequestsOutsideOfficeAfter"
            nameDefault="serviceRequestsOutsideOfficeAfterDefault"
            defaultPrice={serviceRequestsOutsideOfficeHoursAfterDefault}
            price={serviceRequestsOutsideOfficeHoursAfterPrice}
            watcher={watcher}
            setValue={setValue}
            register={register}
            errors={errors}
            canEdit={canEdit}
          />
        </Col>

        <Col xs={4} sm={4} md={4} lg={6} data-cy="billing-rules-incident">
          <DetailsTitle css={{ margin: "2rem 0 1rem 0rem" }}>
            Incident
          </DetailsTitle>

          <SwitchToDefaultValue
            label="Office Hours"
            name="incidentsOffice"
            nameDefault="incidentsOfficeDefault"
            defaultPrice={incidentsOfficeDefault}
            price={incidentsOfficePrice}
            watcher={watcher}
            setValue={setValue}
            register={register}
            errors={errors}
            canEdit={canEdit}
          />

          <SwitchToDefaultValue
            label="Outside Office Hours - Till 00:00"
            name="incidentOutsideOfficeHoursBefore"
            nameDefault="incidentOutsideOfficeHoursBeforeDefault"
            defaultPrice={incidentsOutsideOfficeHoursBeforeDefault}
            price={incidentsOutsideOfficeHoursBeforePrice}
            watcher={watcher}
            setValue={setValue}
            register={register}
            errors={errors}
            canEdit={canEdit}
          />

          <SwitchToDefaultValue
            label="Outside Office Hours - After 00:00"
            name="incidentOutsideOfficeHoursAfter"
            nameDefault="incidentOutsideOfficeHoursAfterDefault"
            defaultPrice={incidentsOutsideOfficeHoursAfterDefault}
            price={incidentsOutsideOfficeHoursAfterPrice}
            watcher={watcher}
            setValue={setValue}
            register={register}
            errors={errors}
            canEdit={canEdit}
          />
        </Col>
      </Row>

      {canEdit && (
        <Button disabled={dateError} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      )}
    </Box>
  );
}

export { BillingRulesFormEnvironment };
